import { PenLine, Trash2, Upload, UploadCloud, X } from "lucide-react";
import React from "react";
import {
  ControllerRenderProps,
  FieldPath,
  FieldValues,
  UseFormStateReturn,
} from "react-hook-form";
import ImageUploading, {
  ImageListType,
  ImageType,
} from "react-images-uploading";
import { toast } from "sonner";

import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Skeleton } from "../ui/skeleton";

import FormWrapper from "./custom-form-fields/FormWrapper";

import placeholderImg from "@/assets/images/empty.jpg";
import pdfImg from "@/assets/images/icons/pdf.svg";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { allowedImageTypes, imgBaseUrl } from "@/constants/constants";
import {
  convertImageFormat,
  fileImagePreviewer,
  splitImageFileName,
  supportedFormats,
} from "@/lib/file-image.utils";
import { cn, formatSizeToKB } from "@/lib/utils";

export const getPreviewImage = ({ data }: { data: ImageType }): string => {
  const { dataURL = "", file } = data;

  if (dataURL && !file) {
    // Check if dataURL corresponds to a PDF
    return dataURL.includes(".pdf") ? pdfImg : dataURL;
  }

  if (file) {
    const { type = "" } = file;

    // Determine preview based on file type
    if (type.includes("image")) {
      return dataURL || placeholderImg;
    }

    if (type.includes("application")) {
      return pdfImg;
    }
  }

  // Default fallback
  return placeholderImg;
};

type ExtendedImageListType = ImageListType & {
  name?: string;
  size?: number;
};

type FileContextType = {
  images: ExtendedImageListType;
  setImages: (images: ExtendedImageListType) => void;
  isForFileUpload: boolean;
  setIsForFileUpload: (isForFileUpload: boolean) => void;
  acceptFileTypes: string[];
  setAcceptFileTypes: (fileTypes: string[]) => void;
  isConverting: boolean; // Add this
  setIsConverting: (isConverting: boolean) => void; // Add this
  isAllowVideoUpload: boolean;
  setIsAllowVideoUpload: (isAllowVideoUpload: boolean) => void;
};

export const isHttps = (url: string): boolean => url.startsWith("https");

const FileContext = React.createContext<FileContextType | undefined>(undefined);

export const useFileContext = (): FileContextType => {
  const context = React.useContext(FileContext);
  if (!context) {
    throw new Error("useFileContext must be used within a FileProvider");
  }

  return context;
};

export const FileProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [images, setImages] = React.useState<ExtendedImageListType>([]);
  const [isForFileUpload, setIsForFileUpload] = React.useState<boolean>(false);
  const [isAllowVideoUpload, setIsAllowVideoUpload] =
    React.useState<boolean>(false);
  const [acceptFileTypes, setAcceptFileTypes] = React.useState<string[]>([]);
  const [isConverting, setIsConverting] = React.useState<boolean>(false);

  return (
    <FileContext.Provider
      value={{
        images,
        setImages,
        isForFileUpload,
        setIsForFileUpload,
        acceptFileTypes,
        setAcceptFileTypes,
        isConverting,
        setIsConverting,
        isAllowVideoUpload,
        setIsAllowVideoUpload,
      }}
    >
      {children}
    </FileContext.Provider>
  );
};

const LoadingOverlay: React.FC = () => (
  <div className='absolute inset-0 rounded-md flex items-center z-10 px-1 gap-2 bg-background'>
    <Skeleton className='h-9 w-10' />
    <Skeleton className='h-9 w-full' />
  </div>
);

interface FileProps {
  onImageUpload: () => void;
  className?: string;
  isForVideoUpload?: boolean;
}
const FileInput = ({
  onImageUpload,
  className,
  isForVideoUpload,
  ...props
}: FileProps): JSX.Element => {
  let accept = "";
  if (isForVideoUpload) {
    accept += (accept ? ", " : "") + "video/*";
  }
  accept = accept || "application/*";

  return (
    <div
      className={cn(
        `flex justify-between border p-1 max-h-11 rounded-md`,
        className,
      )}
      {...props}
    >
      <div className='flex flex-1'>
        <Input
          icon={{
            icon: UploadCloud,
            position: "left",
          }}
          accept={accept}
          className={cn(
            "border-0 h-8 w-full cursor-pointer active:border-0 focus:border-0 ring-0 text-xs focus-visible:ring-transparent shadow-none",
          )}
          onClick={onImageUpload}
          placeholder='Drop/Click here to Upload'
          readOnly
        />
      </div>
      <Button
        variant={"secondary"}
        className='h-8 py-0 text-aldgray'
        onClick={onImageUpload}
      >
        <Upload size={18} className='mr-2' />
        Upload
        <span className='sr-only'>Upload</span>
      </Button>
    </div>
  );
};

type MultipleImagePreviewProps = {
  imageList: ExtendedImageListType;
  onImageRemove: (index: number) => void;
  onImageUpload: () => void;
  onRemove?: (url: string) => void;
  maxFilePreview?: number;
};

const MultipleImagePreview: React.FC<MultipleImagePreviewProps> = ({
  imageList,
  onImageRemove,
  onImageUpload,
  onRemove,
  maxFilePreview,
}) => {
  const maxPreview = imageList.slice(0, maxFilePreview);
  const { isForFileUpload, isAllowVideoUpload } = useFileContext();
  const title = isForFileUpload ? "Selected Files" : "Selected Images";
  const subTitle = isForFileUpload ? "Add More File?" : "Add More Photo?";

  return (
    <div className='flex gap-2 justify-between w-full items-center border border-input p-[2px] rounded-sm'>
      <div className='flex gap-1 '>
        {maxPreview?.map((file, index) => {
          return (
            <div key={index} className='flex flex-1'>
              <img
                src={getPreviewImage({ data: file })}
                alt='uploaded image'
                className='object-fill w-10 rounded-sm min-w-8 h-9'
              />
            </div>
          );
        })}
      </div>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant={"secondary"} className='py-0 h-9 '>
            <PenLine className='w-4 h-4 mr-2 ' />
            Edit
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className='mt-1 space-y-2.5' align='end'>
          <DropdownMenuGroup className='px-2 space-y-2'>
            <DropdownMenuLabel className='px-0 pb-0'>{title}</DropdownMenuLabel>
            {imageList?.map((file, index) => {
              return (
                <DropdownMenuItem
                  key={index}
                  className=' border border-input !hover:bg-transparent focus:bg-transparent'
                >
                  <div className='flex items-center flex-1 gap-2'>
                    <img
                      src={getPreviewImage({
                        data: file,
                      })}
                      alt='uploaded image'
                      className='object-fill w-12 rounded-sm h-9'
                    />
                    <div>
                      <h2 className=' text-xs font-semibold max-w-[80%] truncate'>
                        {file.name}
                      </h2>
                      <p className=' text-xs !font-thin text-gray-400'>
                        {formatSizeToKB(file?.size)}
                      </p>
                    </div>
                  </div>
                  <Button
                    className='text-red-800 bg-transparent hover:bg-[#F9E0E0] h-8 px-1 '
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (file?.dataURL && isHttps(file.dataURL)) {
                        if (onRemove) {
                          onRemove(file.dataURL);
                        }
                      }
                      onImageRemove(index);
                    }}
                  >
                    <Trash2 size={18} color='red' />
                    <span className='sr-only'>remove image </span>
                  </Button>
                </DropdownMenuItem>
              );
            })}
          </DropdownMenuGroup>
          <DropdownMenuGroup className='px-2 space-y-2 '>
            <DropdownMenuLabel className='px-0 pb-0'>
              {subTitle}
            </DropdownMenuLabel>
            <FileInput
              className='p-1 mb-2 border '
              onImageUpload={onImageUpload}
              isForVideoUpload={isAllowVideoUpload}
            />
          </DropdownMenuGroup>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

type FileInputWrapperProps = {
  isMultiple?: boolean;
  maxFiles?: number;
  className?: string;
  onRemove?: (url: string) => void;
  maxFilePreview?: number;
  fileLimit?: number;
  excludeFileLimit?: boolean;
};

export const FileInputWrapper: React.FC<FileInputWrapperProps> = ({
  isMultiple = false,
  maxFiles = 69,
  className,
  onRemove,
  maxFilePreview = 4,
  excludeFileLimit = false,
  fileLimit = 2 * 1024 * 1024,
}) => {
  const {
    images,
    setImages,
    isForFileUpload,
    acceptFileTypes,
    setIsConverting,
    isConverting,
  } = useFileContext();
  const maxNumber = isMultiple ? maxFiles : 1;

  const onChange = async (imageList: ImageListType): Promise<void> => {
    if (imageList.length === 0) {
      // This is set to undefined so we can handle it to remove on context
      setImages([
        {
          dataURL: undefined,
          file: undefined,
          name: undefined,
          size: undefined,
        },
      ]);

      return;
    } else {
      const needsConversion = imageList.some(
        (image) => image.file && supportedFormats.includes(image.file.type),
      );

      if (needsConversion) {
        setIsConverting(true);
      }
      try {
        const updateImages = await Promise.all(
          imageList?.map(async (image) => {
            if (image?.dataURL && isHttps(image.dataURL)) {
              return {
                ...image,
                name: splitImageFileName(image?.dataURL, imgBaseUrl),
                size: 0,
              };
            }
            if (!excludeFileLimit) {
              if (image.file && image.file.size > fileLimit) {
                toast.error("File size should not exceed 2 MB");

                return {
                  dataURL: undefined,
                  file: undefined,
                  name: undefined,
                  size: undefined,
                };
              }
            }
            if (image.file && supportedFormats.includes(image.file.type)) {
              const tempFile = await convertImageFormat({
                file: image.file,
                targetFormat: "webp",
              });

              return {
                ...image,
                dataURL: URL.createObjectURL(tempFile as Blob),
                file: tempFile,
                name: image.file?.name,
                size: tempFile?.size,
              };
            }

            return {
              ...image,
              file: image.file,
              name: image.file?.name,
              size: image.file?.size,
            };
          }),
        );
        setImages(
          updateImages.filter(
            (image) => image.name !== undefined,
          ) as ExtendedImageListType,
        );
      } finally {
        if (needsConversion) {
          setIsConverting(false);
        }
      }
    }
  };

  return (
    <div className='relative'>
      {isConverting && <LoadingOverlay />}
      <ImageUploading
        multiple={isMultiple}
        value={images}
        onChange={onChange}
        maxNumber={maxNumber}
        acceptType={acceptFileTypes ?? allowedImageTypes}
        allowNonImageType={isForFileUpload}
      >
        {({ onImageUpload, onImageRemove, dragProps }): JSX.Element => {
          if (images?.length > 0 && isMultiple) {
            return (
              <MultipleImagePreview
                imageList={images}
                onImageRemove={onImageRemove}
                onImageUpload={onImageUpload}
                onRemove={onRemove}
                maxFilePreview={maxFilePreview}
              />
            );
          } else if (images?.length > 0 && !isMultiple) {
            const getExtension = (
              image: File | undefined,
            ): string | undefined => {
              if (!(image instanceof File) || !image) {
                return undefined;
              }
              const [_, extension] = image?.type?.split("/");

              return extension;
            };

            return (
              <div className='grid grid-cols-3 justify-center  items-center w-full border px-[2px] py-[1px]  max-h-10 rounded-sm'>
                <div className='flex items-center col-span-2'>
                  {fileImagePreviewer({
                    file: images[0]?.dataURL ?? "",
                    className: " rounded-sm !size-7 mb-1.5",
                    extension: getExtension(images[0]?.file),
                  })}

                  <div className='flex flex-col justify-center ml-2 max-w-[70%]'>
                    <p className='text-xs truncate text-aldgray '>
                      {images[0]?.name}
                    </p>
                    <p className='text-xs text-aldgray'>
                      {images[0]?.size && `${images[0].size / 1000} KB`}
                    </p>
                  </div>
                </div>
                <Button
                  className='text-red-500 bg-[#F9E0E0] h-8 px-2 '
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onImageRemove(0);
                  }}
                >
                  <X size={18} className='mr-2' color='red' />
                  Remove
                  <span className='sr-only'>remove</span>
                </Button>
              </div>
            );
          }

          //Show File Input
          return (
            <FileInput
              onImageUpload={onImageUpload}
              className={className}
              {...dragProps}
            />
          );
        }}
      </ImageUploading>
    </div>
  );
};

/**
 * Props for the FileUploaderComponent
 * @template TFieldValues Field values used in the form
 * @template TName The field name from TFieldValues
 */
type FileUploaderProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
> = {
  field: ControllerRenderProps<TFieldValues, TName>;
  formState: UseFormStateReturn<TFieldValues>;
  className?: string;
  isMultiple?: boolean;
  maxFiles?: number;
  onRemove?: (url: string) => void;
  labelsProps?: {
    label?: string;
    labelClassName?: string;
    isRequired?: boolean;
    showLabel?: boolean;
  };
  placeholder?: string;
  maxFilePreview?: number;
  isForFileUpload?: boolean;
  acceptFileTypes?: string[]; //Uploadable file types Note: Don't use "." just the file extension eg. ["pdf", "docx"] don't use [".pdf", ".docx"]
  allowVideoUpload?: boolean;
  fileLimit?: number;
  excludeFileLimit?: boolean;
};

export const FileUploader = <
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
>(
  props: FileUploaderProps<TFieldValues, TName>,
): React.JSX.Element => {
  const { field, labelsProps, formState } = props;
  const {
    images,
    setImages,
    setIsForFileUpload,
    setAcceptFileTypes,
    setIsAllowVideoUpload,
  } = useFileContext();

  //Save original images
  const originalDefaultValues =
    formState?.defaultValues &&
    (formState?.defaultValues[field.name] as string[] | File[]);

  //Handle Form Reset or Discard
  const handleFormReset = (): void => {
    if (originalDefaultValues) {
      const updateImages = originalDefaultValues.map((value: string | File) => {
        if (typeof value === "string" && isHttps(value)) {
          return {
            dataURL: value,
            name: splitImageFileName(value, imgBaseUrl),
            size:
              Math.floor(
                Math.random() * (1.7 * 1024 * 1024 - 1 * 1024 * 1024 + 1),
              ) +
              1 * 1024 * 1024, // This is randomly generated size for now
          };
        }

        return {
          file: value as File,
          name: (value as File)?.name,
          size: (value as File)?.size,
        };
      });

      setImages(updateImages as ExtendedImageListType);
    } else {
      setImages([]);
    }
  };

  // Sync Form default values to File Context (runs only on initial load)
  React.useEffect(() => {
    if (field.value === null || field.value === undefined) {
      return;
    }
    if (field.value && images.length === 0) {
      const updateImages = field.value?.map((value: string | File) => {
        //handle Values being Null
        if (!value) {
          return {
            dataURL: undefined,
            file: undefined,
            name: undefined,
            size: undefined,
          };
        }
        if (typeof value === "string" && isHttps(value)) {
          return {
            dataURL: value,
            name: splitImageFileName(value, imgBaseUrl),
            size:
              Math.floor(
                Math.random() * (1.7 * 1024 * 1024 - 1 * 1024 * 1024 + 1),
              ) +
              1 * 1024 * 1024, // This is randomly generated size for now
          };
        }

        return {
          file: value as File,
          name: (value as File)?.name,
          size: (value as File)?.size,
        };
      });

      setImages(updateImages as ExtendedImageListType);
    }
  }, [field.value, images.length, setImages]);

  // Sync Form default values to File Context (runs only on initial load)
  React.useEffect(() => {
    // If the form is reset or discarded
    // then update the images to the original default values
    if (JSON.stringify(originalDefaultValues) === JSON.stringify(field.value)) {
      handleFormReset();
    }
  }, [JSON.stringify(originalDefaultValues) === JSON.stringify(field.value)]);

  React.useEffect(() => {
    if (images.length > 0) {
      if (
        (images[0].dataURL === undefined && images[0]?.file === undefined) ||
        images[0].file === null
      ) {
        field?.onChange([]);
        setImages([]);

        return;
      }
      const updatedValues = images.map((image) =>
        image?.dataURL && isHttps(image.dataURL) ? image?.dataURL : image?.file,
      );

      field?.onChange([...updatedValues]);
    }
  }, [images]);

  React.useEffect(() => {
    setIsForFileUpload(Boolean(props.isForFileUpload));
    setAcceptFileTypes(props.acceptFileTypes ?? []);
    setIsAllowVideoUpload(Boolean(props.allowVideoUpload));
  }, [props.isForFileUpload, props.acceptFileTypes, props.allowVideoUpload]);

  return (
    <FormWrapper
      label={!labelsProps?.showLabel ? "" : labelsProps?.label}
      isRequired={labelsProps?.isRequired}
      className={cn(
        labelsProps?.labelClassName,
        !labelsProps?.showLabel ? "space-y-0" : "",
      )}
    >
      <FileInputWrapper
        isMultiple={props.isMultiple}
        maxFiles={props.maxFiles}
        className={props.className}
        onRemove={props.onRemove}
        maxFilePreview={props.maxFilePreview}
      />
    </FormWrapper>
  );
};

export const FileUploaderComponent = <
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
>(
  props: FileUploaderProps<TFieldValues, TName>,
): React.JSX.Element => {
  return (
    <FileProvider>
      <FileUploader {...props} />
    </FileProvider>
  );
};

FileUploaderComponent.displayName = "FileUploaderComponent";
